"use client";

import { IconPlay } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertWarningOutline18, IconUploadOutline18 } from "nucleo-ui-outline-18";
import ConfirmModal from "@/components/custom/ConfirmModal";
import ResponsiveModal from "@/components/custom/ResponsiveModal";
import { Badge } from "@/components/ui/badge";
import { GlassIcon } from "@/components/ui/glass-icon";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { toast } from "@/lib/toast";
import { useConnectorsStore } from "@/stores/connectorsStore";
import type { WorkflowInput } from "@/stores/workflowsStore";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useMemo, useState } from "react";
import { ConnectorLogo } from "../../connectors/_components/ConnectorLogo";
import { resolveArchetype } from "./test-run/archetypes/registry";
import type { PrimaryAction } from "./test-run/archetypes/types";
import { computeAutoTriggerInputWarning } from "./test-run/computeAutoTriggerInputWarning";
import { type ResultSchema, AUTO_GENERATED_TRIGGER_FIELDS, resolvePropsInPayload } from "./test-run/sampleGenerators";
export { AUTO_GENERATED_TRIGGER_FIELDS };

/** Info about a money-touching action node for the amount adjustment UI */
export interface MoneyActionInfo {
  nodeId: string;
  nodeName: string;
  actionType: string;
  kind: "send" | "swap" | "bridge" | "disperse" | "receive";
  payloadAmountField: string;
  currentAmount: string;
  symbol: string;
  chainName: string;
  decimals: number;
  chainId?: number;
  tokenAddress?: string;
  /** When true, amounts are already human-readable (not raw/wei) and should not be converted. */
  isHumanReadableAmount?: boolean;
}

/** Convert a raw-unit amount string to a human-readable decimal string */
export function rawToDecimal(raw: string, decimals: number): string {
  if (!raw || raw === "0") return "0";
  if (raw.includes("{{")) return raw;
  if (raw.includes(".")) return raw;
  const cleaned = raw.replace(/[^0-9]/g, "");
  if (!cleaned || cleaned === "0") return "0";
  if (decimals === 0) return cleaned;
  const padded = cleaned.padStart(decimals + 1, "0");
  const whole = padded.slice(0, padded.length - decimals).replace(/^0+(?=\d)/, "") || "0";
  const fractional = padded.slice(padded.length - decimals).replace(/0+$/, "");
  return fractional ? `${whole}.${fractional}` : whole;
}
type WorkflowStatus = "draft" | "active" | "paused" | "archived";
interface TriggerInputModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resultSchema: ResultSchema | undefined;
  triggerType: string;
  triggerName?: string;
  triggerPayload?: Record<string, unknown>;
  connectorId?: string;
  onRun: (payload: Record<string, unknown>, options?: {
    dustTest?: boolean;
    skipBalanceCheck?: boolean;
    simulate?: boolean;
  }) => void | Promise<void>;
  isRunning?: boolean;
  inputSchema?: WorkflowInput[];
  workflowId?: string;
  supportsLiveTest?: boolean;
  executionMode?: "test" | "run";
  propValues?: Record<string, unknown>;
  hasMoneyActions?: boolean;
  moneyActions?: MoneyActionInfo[];
  workflowStatus?: WorkflowStatus;
  hasLiveVersion?: boolean;
  onPublish?: () => void | Promise<void>;
  onPause?: () => void | Promise<void>;
  onResume?: () => void | Promise<void>;
  isPublishing?: boolean;
  isResuming?: boolean;
  isArchived?: boolean;
}
export default function TriggerInputModal({
  open,
  onOpenChange,
  resultSchema,
  triggerType,
  triggerName,
  triggerPayload,
  connectorId,
  onRun,
  isRunning = false,
  inputSchema,
  workflowId,
  supportsLiveTest = false,
  executionMode = "test",
  propValues,
  hasMoneyActions = false,
  moneyActions = [],
  workflowStatus,
  hasLiveVersion = false,
  onPublish,
  onPause,
  onResume,
  isPublishing = false,
  isResuming = false,
  isArchived = false
}: TriggerInputModalProps) {
  const definition = useDefinitionByType(triggerType);
  const connectorTypes = useConnectorsStore(state => state.connectorTypes);
  const isPublished = executionMode === "run" || workflowStatus === "active" || workflowStatus === "paused";
  // NOTE(UX): For cross-chain workflows (e.g. relay-swap), user picks "Real execution, dust
  // amounts" but the backend silently falls back to simulation. Temporarily accepting this —
  // if users report confusion, fix by detecting cross-chain here (sent.chainId !==
  // received.chainId from fundsMovement metadata) and disabling the dust test option.
  const hasDustTesting = moneyActions.length > 0;
  const [testMode, setTestMode] = useState<"simulate" | "dust">("simulate");
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const [showResumeConfirm, setShowResumeConfirm] = useState(false);
  const resolvedTriggerPayload = useMemo(() => resolvePropsInPayload(triggerPayload || {}, propValues), [triggerPayload, propValues]);

  // B3-3603 / CS-6: surface a banner when an auto-firing trigger has required
  // inputSchema fields that will be undefined at runtime. The same logic runs
  // server-side at publish time (validator.AnalyzeWarnings); we recompute here
  // so the warning shows up immediately as the user edits, without waiting on
  // a publish round-trip.
  const autoTriggerInputWarning = useMemo(() => computeAutoTriggerInputWarning(triggerType, inputSchema), [triggerType, inputSchema]);

  // Archetype resolution
  const archetypeResolved = useMemo(() => resolveArchetype(triggerType, isPublished), [triggerType, isPublished]);
  const isRunMode = executionMode === "run";
  const primaryActionLabel = isRunMode ? "Run Workflow" : testMode === "simulate" ? "Run test" : "Run Dust Test";
  const hideControlsWhenPublished = archetypeResolved.config.hideExecutionControlsWhenPublished && isPublished;
  const disableRunButtonWhenPublished = archetypeResolved.config.disableRunButtonWhenPublished && isPublished;
  const showExecutionModeSelector = !isRunMode && !hideControlsWhenPublished && hasDustTesting;
  const subtitle = archetypeResolved.config.description;
  const headerLogo = useMemo(() => {
    if (archetypeResolved.config.headerVisual?.kind !== "connector-logo") return null;
    const connectorType = definition?.connector?.type;
    const logoUrl = definition?.logoUrl || (connectorType ? connectorTypes.find(ct => ct.type === connectorType)?.logoUrl : null);
    if (!logoUrl) return null;
    return {
      type: connectorType || triggerType.split("-")[0] || "b3os",
      logoUrl,
      name: definition?.name || triggerName || "B3OS"
    };
  }, [archetypeResolved.config.headerVisual?.kind, connectorTypes, definition?.connector?.type, definition?.logoUrl, definition?.name, triggerName, triggerType]);
  const handleClose = useCallback((nextOpen: boolean) => {
    if (!nextOpen) {
      setTestMode("simulate");
      setArchetypePayload(null);
      setCustomPrimaryAction(null);
    }
    onOpenChange(nextOpen);
  }, [onOpenChange]);
  const canShowPublishToggle = !!workflowStatus && !isArchived && (onPublish || onPause || onResume);
  const handlePublishToggle = useCallback((checked: boolean) => {
    if (checked) {
      if (workflowStatus === "paused" && onResume) {
        handleClose(false);
        setShowResumeConfirm(true);
      } else if (onPublish) {
        handleClose(false);
        setShowPublishConfirm(true);
      }
      return;
    }
    if (onPause) {
      handleClose(false);
      setShowUnpublishConfirm(true);
    }
  }, [handleClose, onPause, onPublish, onResume, workflowStatus]);

  // Unified run callback — strips _-prefixed metadata before sending to backend
  const handleRunWithPayload = useCallback(async (payload: Record<string, unknown>) => {
    const cleanPayload = Object.fromEntries(Object.entries(payload).filter(([k]) => !k.startsWith("_")));
    try {
      await onRun(cleanPayload, {
        dustTest: testMode === "dust" || undefined,
        simulate: executionMode === "run" ? false : testMode === "simulate"
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to run workflow.";
      toast.error(message);
    }
  }, [onRun, testMode, executionMode]);

  // Archetypes call onRun to "select" a payload; the shell stores it and
  // the Run button (after execution mode) triggers the actual run.
  const [archetypePayload, setArchetypePayload] = useState<Record<string, unknown> | null>(null);
  // Archetypes can register a custom primary CTA (e.g. "Start Listening") that
  // replaces the default footer run button.
  const [customPrimaryAction, setCustomPrimaryAction] = useState<PrimaryAction | null>(null);
  const showRunButton = !hideControlsWhenPublished && !(isPublished && triggerType === "manual" && !archetypePayload);
  return <>
      <ResponsiveModal open={open} onOpenChange={handleClose} title="" contentClass="max-w-2xl">
        <div className="flex flex-col gap-4">
          <div className="border-ds-border-default mb-2 flex items-center gap-3 border-b pb-6">
            {headerLogo ? <ConnectorLogo type={headerLogo.type} name={headerLogo.name} logoUrl={headerLogo.logoUrl} size="md" /> : <GlassIcon icon={IconPlay} color="amber" size="lg" />}
            <div>
              <h2 className="text-ds-text-primary text-base font-semibold">
                {executionMode === "run" ? "Run Live Workflow" : "Test Workflow"}
              </h2>
              <p className="label-xs mt-0.5">{subtitle}</p>
            </div>
          </div>

          {/* B3-3603 / CS-6: warn when an auto-firing trigger has required runtime
              inputs that will never be provided at fire-time. Banner is informational
              only — the user can still test/run the workflow. */}
          {autoTriggerInputWarning && <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/40" role="alert">
              <div className="flex items-center gap-2">
                <IconAlertWarningOutline18 className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium uppercase tracking-wider text-amber-900 dark:text-amber-300">
                  Auto trigger with required inputs
                </span>
              </div>
              <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-200">
                {autoTriggerInputWarning.message}
              </p>
              <div className="flex flex-wrap gap-1">
                {autoTriggerInputWarning.fields.map(field => <span key={field} className="text-2xs rounded bg-amber-100 px-1.5 py-0.5 font-mono text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                    {field}
                  </span>)}
              </div>
            </div>}

          {/* Trigger data via archetype */}
          <div>
            <archetypeResolved.Component triggerType={triggerType} triggerName={triggerName} triggerPayload={resolvedTriggerPayload} resultSchema={resultSchema} inputSchema={inputSchema} workflowId={workflowId} supportsLiveTest={supportsLiveTest ?? false} propValues={propValues} onRun={setArchetypePayload} isRunning={isRunning ?? false} isPublished={isPublished} connectorId={connectorId} onRegisterPrimaryAction={setCustomPrimaryAction} />
          </div>

          {/* Execution mode */}
          {showExecutionModeSelector && <div className="border-ds-border-default space-y-3 border-t pt-4">
              <RadioGroup value={testMode} onValueChange={value => setTestMode(value as "simulate" | "dust")} className="gap-0">
                <div className="divide-ds-neutral-100 dark:divide-ds-neutral-800 divide-y">
                  <div className="flex items-center gap-6 py-4 pl-4 first:pt-0">
                    <RadioGroupItem value="simulate" className="flex-shrink-0" />
                    <button type="button" onClick={() => setTestMode("simulate")} className="flex flex-col text-left">
                      <span className="body-sm-semibold text-ds-text-primary">Simulate</span>
                      <p className="caption-xs mt-0.5">
                        {hasMoneyActions ? "On-chain transactions are simulated. Nothing executes for real." : "Test data is generated. Nothing actually sends or executes."}
                      </p>
                    </button>
                  </div>

                  {hasDustTesting && <div className="flex items-center gap-6 py-4 pl-4">
                      <RadioGroupItem value="dust" className="flex-shrink-0" />
                      <button type="button" onClick={() => setTestMode("dust")} className="flex flex-col text-left">
                        <span className="body-sm-semibold text-ds-text-primary flex items-center gap-2">
                          Real execution, dust amounts
                          <Badge variant="info" className="label-2xs px-3">
                            Recommended
                          </Badge>
                        </span>
                        <p className="caption-xs mt-0.5">
                          Fire real transactions with ~$0.01 USD amounts to verify gas, approvals, and receipts.
                        </p>
                      </button>
                    </div>}
                </div>
              </RadioGroup>
            </div>}

          {/* Footer: publish toggle / live switch + run button */}
          <div className="border-ds-border-default flex items-center justify-between border-t pt-4">
            <div className="flex min-h-9 flex-col items-start justify-center">
              {canShowPublishToggle && workflowStatus === "draft" && !!onPublish && <div className="space-y-2 pl-1">
                  <Button size="sm" onClick={() => handlePublishToggle(true)} disabled={isPublishing || isResuming} aria-label="Publish workflow">
                    <span className="flex items-center gap-2 text-xs">
                      {isPublishing ? <PixelLoader size="xs" aria-label="Publishing" /> : <IconUploadOutline18 className="h-3 w-3" aria-hidden="true" />}
                      <span>{isPublishing ? "Publishing" : "Publish"}</span>
                    </span>
                  </Button>
                  <p className="caption-xs">Publish your workflow to run it for real.</p>
                </div>}
            </div>

            {showRunButton && (customPrimaryAction ? <Button size="sm" className="px-6" onClick={customPrimaryAction.onClick} disabled={isRunning || !!customPrimaryAction.disabled || !!disableRunButtonWhenPublished}>
                  {customPrimaryAction.leadingIcon ? <span className="mr-2 inline-flex h-3.5 w-3.5 items-center justify-center">
                      {customPrimaryAction.leadingIcon}
                    </span> : null}
                  {customPrimaryAction.label}
                  {customPrimaryAction.trailingIcon ? <span className="ml-2 inline-flex h-3.5 w-3.5 items-center justify-center">
                      {customPrimaryAction.trailingIcon}
                    </span> : null}
                </Button> : <Button size="sm" className="px-6" onClick={() => archetypePayload && handleRunWithPayload(archetypePayload)} disabled={isRunning || !archetypePayload || !!disableRunButtonWhenPublished}>
                  {isRunning && <PixelLoader size="xs" className="mr-2" />}
                  {isRunning ? "Running..." : primaryActionLabel}
                  {!isRunning && <IconPlay className="ml-2 h-3.5 w-3.5" />}
                </Button>)}
          </div>
        </div>
      </ResponsiveModal>
      <ConfirmModal open={showPublishConfirm} onOpenChange={setShowPublishConfirm} title="Publish Workflow?" description={hasLiveVersion ? "This will overwrite the current live version with your draft changes. The live workflow will be updated immediately." : "This will make your workflow live and available for execution. Any configured triggers will start firing."} confirmText={isPublishing ? "Publishing..." : "Publish"} cancelText="Cancel" onConfirm={() => {
      onPublish?.();
      handleClose(false);
    }} isLoading={isPublishing} />
      <ConfirmModal open={showUnpublishConfirm} onOpenChange={setShowUnpublishConfirm} title="Unpublish Workflow?" description="This will take your workflow offline. All triggers will stop firing and scheduled runs will be paused until you publish again." confirmText="Unpublish" cancelText="Cancel" variant="destructive" onConfirm={() => {
      onPause?.();
      handleClose(false);
    }} />
      <ConfirmModal open={showResumeConfirm} onOpenChange={setShowResumeConfirm} title="Resume Workflow?" description="This will put your workflow back online. Triggers will start firing again and scheduled runs will resume." confirmText={isResuming ? "Resuming..." : "Resume"} cancelText="Cancel" onConfirm={() => {
      onResume?.();
      handleClose(false);
    }} isLoading={isResuming} />
    </>;
}